import { CloseCircleFilled, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Checkbox,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Switch,
  Upload,
  message,
} from "antd";
import { Box } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setCountry } from "../../../store/reducers/country-reducer";
import axiosInstance from "../../../axios/axios-instance";
import api from "../../../hooks/api";
import { useMe } from "../../../auth/MeContext";
import { RoleScopeType } from "../../../@type/role.types";
import { KeyValueObject } from "../../../utils/models";
import { pathAPI } from "../../../utils/pathApi";
import { REGEX_PASSWORD } from "../../../utils/regex";

interface SalonOption {
  key: number;
  title: string;
}

type CountryAssignmentMode = "SHARE" | "TRANSFER";

interface CountryConflict {
  country_id: number;
  holders: { id: number; full_name: string; email: string }[];
}

interface OutletDto {
  id: number;
  business_name: string;
}

interface RoleOption {
  value: number;
  label: string;
  scopeType: RoleScopeType;
  multiCountry: boolean;
}

interface AdminDetailLocation {
  id: number;
  name?: string;
  code?: string;
  i18n_names?: Record<string, string>;
}

interface AdminDetailResponse {
  full_name?: string;
  email?: string;
  role_id?: number;
  role_name?: string;
  role?: { name?: string };
  can_edit?: boolean;
  avatar?: string | null;
  outlets?: OutletDto[];
  locations?: AdminDetailLocation[];
}

interface AddAdminModalProps {
  controlModal: boolean;
  closeModal: () => void;
  reLoadDataTable?: () => void;
  adminId?: any;
  isEditAdmin: boolean;
}

const AddAdmin: React.FC<AddAdminModalProps> = ({
  controlModal,
  closeModal,
  reLoadDataTable,
  adminId,
  isEditAdmin,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  
  // Get data from Redux
  const reduxCountries = useSelector((state: any) => state.countries.countries);
  const reduxLanguage = useSelector((state: any) => state.language.currentLanguage);
  
  // Use memoized values to prevent re-renders
  const countries = reduxCountries.length > 0 
    ? reduxCountries 
    : (localStorage.getItem("countries") ? JSON.parse(localStorage.getItem("countries") || '[]') : []);
    
  const currentLanguage = reduxLanguage || localStorage.getItem("language") || '';
  
  const { me, isSuperAdmin } = useMe();
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const selectedRoleId = Form.useWatch("roleId", form);
  const selectedRole = roleOptions.find((r) => r.value === selectedRoleId);
  const scopeType: RoleScopeType = selectedRole?.scopeType ?? "COUNTRY";
  const multiSelectLocation = scopeType === "COUNTRY" && !!selectedRole?.multiCountry;
  const [location, setLocation] = useState<string[]>([]);
  const [showLocations, setShowLocation] = useState<string[]>([]);
  const [countryOptions, setCountryOptions] = useState<KeyValueObject[]>([]);
  const [avatar, setAvatar] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [isLoading, setIsloading] = useState(false);
  
  // Use ref to track initialization
  const initialized = useRef(false);
  const detailsFetched = useRef<unknown>(null);
  // Bumps per detail fetch so a late response for a previous admin is ignored.
  const requestSeqRef = useRef(0);
  const originalLocation = useRef<string[]>([]);
  const [conflictPrompt, setConflictPrompt] = useState<{
    conflicts: CountryConflict[];
    value: any;
  } | null>(null);

  // Countries are cached at login; reload when the cache is empty (e.g. login-time fetch failed)
  useEffect(() => {
    if (countries && countries.length > 0) return;
    api
      .GET(pathAPI.countries, {}, {}, true)
      .then((data) => {
        dispatch(setCountry(data.data));
        localStorage.setItem("countries", JSON.stringify(data.data));
      })
      .catch((e) => message.error(e.message));
  }, [countries?.length]);

  // Only update country options when dependencies actually change
  useEffect(() => {
    if (countries && countries.length > 0) {
      try {
        const allowedIds = isSuperAdmin ? null : new Set((me?.countries ?? []).map((c) => c.id));
        const visibleCountries = allowedIds
          ? countries.filter((item: any) => allowedIds.has(item.id))
          : countries;
        const countriesOption = visibleCountries.map((item: any) => ({
          key: item.id,
          value: item.name || (item.i18n_names?.[currentLanguage]) || item.code,
          code: item.code, // Keep code for API calls
        }));
        setCountryOptions(countriesOption);
      } catch (error) {
        console.error("Error processing countries:", error);
      }
    }
  }, [JSON.stringify(countries), currentLanguage, isSuperAdmin, me]);

  // Roles the current admin may assign (server decides: assignable table + permission subset)
  useEffect(() => {
    if (!controlModal) return;
    axiosInstance
      .get(pathAPI.rolesAssignable)
      .then((res) => {
        const options: RoleOption[] = (res.data?.data ?? []).map((r: any) => ({
          value: r.id,
          label: r.name,
          scopeType: r.scope_type,
          multiCountry: !!r.multi_country,
        }));
        setRoleOptions((prev) => {
          const kept = prev.filter((p) => !options.some((o) => o.value === p.value) && p.value === form.getFieldValue("roleId"));
          return [...options, ...kept];
        });
        if (!isEditAdmin && options.length > 0 && !form.getFieldValue("roleId")) {
          form.setFieldsValue({ roleId: options[0].value });
        }
      })
      .catch(() => message.error("Failed to load roles"));
  }, [controlModal, isEditAdmin]);

  // SALON ASSIGNMENT
  const [salonList, setListSalon] = useState<SalonOption[]>([]);
  const [ownedSalons, setOwnedSalons] = useState<SalonOption[]>([]);
  const [targetKeys, setTargetKeys] = useState<number[]>([]);
  const [salonSearch, setSalonSearch] = useState("");

  const toSalonOption = (outlet: OutletDto): SalonOption => ({
    key: outlet.id,
    title: outlet.business_name,
  });

  const filteredSalons = salonList.filter((item) =>
    item.title.toLowerCase().includes(salonSearch.trim().toLowerCase())
  );

  const onToggleSalon = (key: number, checked: boolean) => {
    setTargetKeys((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key)
    );
  };

  // Salons selectable for a leader: unassigned salons within the chosen countries
  // (server also filters by the current admin's scope) + salons already assigned to this user
  useEffect(() => {
    if (!controlModal || scopeType !== "OUTLET" || location.length === 0) {
      setListSalon([]);
      return;
    }
    const params = location.map((id) => `countryIds=${id}`).join("&");
    let cancelled = false;
    axiosInstance
      .get(`${pathAPI.availableOutlet}?${params}`)
      .then((res) => {
        if (cancelled) return;
        const merged = new Map<number, SalonOption>();
        ownedSalons.forEach((item) => merged.set(item.key, item));
        (res.data.data as OutletDto[]).forEach((outlet) =>
          merged.set(outlet.id, toSalonOption(outlet))
        );
        const list = Array.from(merged.values());
        setListSalon(list);
        setTargetKeys((prev) => prev.filter((k) => merged.has(k)));
      })
      .catch(() => message.error("Failed to load salons"));
    return () => {
      cancelled = true;
    };
  }, [controlModal, scopeType, JSON.stringify(location), ownedSalons]);

  const resetPerAdminState = () => {
    form.resetFields();
    setShowLocation([]);
    setLocation([]);
    setTargetKeys([]);
    setOwnedSalons([]);
    setSalonSearch("");
    originalLocation.current = [];
    setAvatar("");
    clearAvatarFile();
  };

  // Handle admin details fetch
  useEffect(() => {
    if (!controlModal || !adminId || !isEditAdmin || detailsFetched.current === adminId) {
      return;
    }
    const seq = ++requestSeqRef.current;
    resetPerAdminState();

    const fetchDetails = async () => {
      try {
        const response = await axiosInstance.get(`${pathAPI.adminUsers}/${adminId}`);
        if (seq !== requestSeqRef.current) return;
        const data: AdminDetailResponse = response.data.data;

        form.setFieldsValue({
          fullName: data.full_name || "",
          email: data.email,
          roleId: data.role_id,
          adminCanEdit: data.can_edit
        });
        const roleId = data.role_id;
        if (roleId) {
          setRoleOptions((prev) =>
            prev.some((r) => r.value === roleId)
              ? prev
              : [
                  ...prev,
                  {
                    value: roleId,
                    label: data.role_name || data.role?.name || "",
                    scopeType: data.locations?.length ? "COUNTRY" : "GLOBAL",
                    multiCountry: (data.locations?.length ?? 0) > 1,
                  },
                ]
          );
        }

        // Handle outlets
        const outlets = Array.isArray(data.outlets) ? data.outlets : [];
        setTargetKeys(outlets.map((salon) => salon.id));
        setOwnedSalons(outlets.map(toSalonOption));

        // Handle locations
        const locations = Array.isArray(data.locations) ? data.locations : [];
        const locationIds = locations.map((item) => item.id);
        const locationNames = locations.map(
          (item) => item.i18n_names?.[currentLanguage] || item.name || item.code || ""
        );
        setLocation(locationIds.map(String));
        setShowLocation(locationNames);
        originalLocation.current = locationIds.map(String);

        setAvatar(data.avatar || "");
        detailsFetched.current = adminId;
      } catch (error) {
        if (seq !== requestSeqRef.current) return;
        console.error("Error fetching admin details:", error);
        message.error("Failed to load admin details");
      }
    };
    
    fetchDetails();
  }, [controlModal, adminId, isEditAdmin]);
  
  // Reset form when opening in add mode
  useEffect(() => {
    if (controlModal && !isEditAdmin && !initialized.current) {
      resetPerAdminState();
      initialized.current = true;
    }
    
    if (!controlModal) {
      initialized.current = false;
      detailsFetched.current = null;
      requestSeqRef.current += 1;
    }
  }, [controlModal, isEditAdmin]);

  /** FUNC SAVE ADMIN */
  const onFinish = async (value: any) => {
    // A country already managed by another admin: let the super admin choose share / transfer
    if (isSuperAdmin && scopeType === "COUNTRY") {
      const addedCountryIds = location.filter((id) => !originalLocation.current.includes(String(id)));
      if (addedCountryIds.length > 0) {
        try {
          const res = await axiosInstance.get(`${pathAPI.adminUsers}/country-conflicts`, {
            params: {
              countryIds: addedCountryIds.join(","),
              excludeAdminId: isEditAdmin ? adminId : undefined,
            },
          });
          const conflicts: CountryConflict[] = res.data?.data ?? [];
          if (conflicts.length > 0) {
            setConflictPrompt({ conflicts, value });
            return;
          }
        } catch (error) {
          message.error("Failed to check country assignment");
          return;
        }
      }
    }
    return submitAdmin(value, "SHARE");
  };

  const onChooseCountryMode = (mode: CountryAssignmentMode) => {
    const prompt = conflictPrompt;
    setConflictPrompt(null);
    if (prompt) {
      submitAdmin(prompt.value, mode);
    }
  };

  const countryName = (id: number) =>
    countryOptions.find((c) => String(c.key) === String(id))?.value ?? String(id);

  const submitAdmin = (value: any, mode: CountryAssignmentMode) => {
    setIsloading(true);
    var formData = new FormData();
    formData.append("fullName", value.fullName);
    if (avatarFile) {
      formData.append("file", avatarFile);
    }
    if (value.password) {
      formData.append("password", value.password);
    }
    formData.append("roleId", String(value.roleId));
    if (scopeType !== "GLOBAL") {
      location.forEach((item) => {
        formData.append("countryIds", item);
      });
    }
    formData.append("email", value.email);
    formData.append("countryAssignmentMode", mode);
    formData.append("canEdit", value.adminCanEdit);
    if (scopeType === "OUTLET") {
      targetKeys.forEach((key) => {
        formData.append("outletIds", String(key));
      });
    }
    if (isEditAdmin && adminId != undefined) {
      formData.append("id", adminId);
    }
    if (isEditAdmin) {
      // CASE UPDATE ADMIN
      return api
        .PUT(pathAPI.adminUsers, formData, {}, true)
        .then((res) => {
          reloadTable();
          setIsloading(false)
        })
        .catch((err: Error) => {
          let res: string = `${err.message}`;
          message.error(res);
        });
    } else {
      // CASE CREATE NEW ADMIN
      return api
        .POST(pathAPI.adminUsers, formData, {}, true)
        .then((res) => {
          // Reload list admin
          reloadTable();
          setIsloading(false)
        })
        .catch((err: Error) => {
          let res: string = `${err.message}`;
          console.log("==== create admin error: ", err);
          
          message.error(JSON.parse(res).message);
          setIsloading(false);
        });
    }
  };

  const reloadTable = () => {
    if (reLoadDataTable != undefined) {
      reLoadDataTable();
    }
    closeModal();
    setShowLocation([]);
    message.success(`${t("help_support_detail.mess_success")}`);
    form.resetFields();
  };

  const onHandleClickCancel = () => {
    closeModal();
    setAvatar("");
    clearAvatarFile();
    setIsloading(false);
    setShowLocation([]);
    if (!isEditAdmin) {
      form.resetFields();
    }
  };

  const onHandleChangeRole = () => {
    // The scope of the new role may differ: start over with country / salon selection
    setLocation([]);
    setShowLocation([]);
    setTargetKeys([]);
  };

  const onHandleChangeLocation = async (e: any, option: any) => {
    var listLocation: any = [];
    var listShowLocation: any = [];
    if (option instanceof Array) {
      listLocation = option.map((item: any) => {
        if (item.key != null && item.key != undefined) {
          return item.key; // Store country ID
        }
      });
      listShowLocation = option.map((item: any) => {
        if (item.key != null && item.key != undefined) {
          return item.value;
        }
      });
    } else {
      listLocation.push(option.key); // Store country ID
      listShowLocation.push(option.value);
    }
    setLocation(listLocation);
    setShowLocation(listShowLocation);
  };

  const clearAvatarFile = () => {
    setAvatarFile(null);
    setAvatarPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
  };

  const handleSelectAvatar = (file: File) => {
    clearAvatarFile();
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    return false; // upload happens with the form submit
  };

  const handleRemoveAvatar = async () => {
    if (avatarFile) {
      // not uploaded yet: just drop the local selection (keep the saved avatar, if any)
      clearAvatarFile();
      return;
    }
    if (!avatar) return;
    if (!isEditAdmin || !adminId) {
      setAvatar("");
      return;
    }
    try {
      await api.DELETE(`${pathAPI.adminUsers}/${adminId}/avatar`, {}, {}, true);
      setAvatar("");
      reLoadDataTable?.();
    } catch (e: any) {
      message.error(e?.message);
    }
  };

  const initialValues = {
    adminCanEdit: true
  };

  return (
    <Modal
      className="[&_.ant-modal-content]:rounded-[10px] [&_.ant-modal-close-icon]:text-white [&_.ant-modal-close-icon:hover]:opacity-100 [&_.ant-modal-close-icon:hover]:rotate-90 [&_.ant-transfer]:justify-between [&_.ant-form-item-control-input-content]:text-left"
      open={controlModal}
      onCancel={onHandleClickCancel}
      footer={
        <Box display="flex" justifyContent="flex-end" gap={1}>
        <CancelButton onClick={onHandleClickCancel}>
          {t("btn.cancel")}
        </CancelButton>
        <ActionButton
          onClick={form.submit}
          disabled={isLoading}
          loading={isLoading}
        >
          {t("btn.ok")}
        </ActionButton>
        </Box>
      }
    >
      <div className="h-[50px] bg-[#ba2156] text-white -mt-5 -ml-6 -mr-6 rounded-t-[10px] leading-[50px] pl-[25px] mb-[10px]">
        {!isEditAdmin
          ? t("modal.add_admin_leader")
          : t("modal.update_admin_leader")}
      </div>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={initialValues}
      >
        <div className="flex justify-between w-full">
          <div className="w-1/2 pr-[5px]">
            {/* FULL NAME */}
            <Form.Item
              label={t("admin_user.add_modal.fullname")}
              name="fullName"
              required
              tooltip={t("mess.required")}
              rules={[{ required: true, message: `${t("mess.required")}` }]}
            >
              <Input
                placeholder={t("admin_user.add_modal.input_fullname")}
                allowClear={true}
              />
            </Form.Item>
            {/* EMAIL */}
            <Form.Item
              label="Email"
              required
              name={"email"}
              tooltip={t("mess.required")}
              rules={[
                { required: true, message: `${t("mess.required")}` },
                { type: "email", message: `${t("mess.not_valid_email")}` },
              ]}
            >
              <Input
                placeholder={t("admin_user.add_modal.input_email")}
                allowClear={true}
                disabled={isEditAdmin}
              />
            </Form.Item>
            {/* PASSWORD */}
            <Form.Item
              label={t("admin_user.add_modal.password")}
              required={!isEditAdmin}
              name={"password"}
              rules={[
                { required: !isEditAdmin, message: `${t("mess.required")}` },
                {
                  pattern: REGEX_PASSWORD,
                  message: (
                    <ul>
                      <li>{t("mess.password_min_8")}</li>
                      <li>{t("mess.password_have_special_character")}</li>
                    </ul>
                  ),
                },
              ]}
              tooltip={t("mess.required")}
            >
              <Input.Password
                placeholder={t("admin_user.add_modal.input_password")}
                allowClear={true}
              />
            </Form.Item>
            {/* PASSWORD CONFIRM */}
            <Form.Item
              label={t("admin_user.add_modal.confirm_password")}
              required={!isEditAdmin}
              name={"passwordConfirm"}
              rules={[
                { required: !isEditAdmin, message: `${t("mess.required")}` },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if ((!value && !getFieldValue("password")) || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(`${t("mess.password_not_match")}`)
                    );
                  },
                }),
              ]}
              tooltip={t("mess.required")}
            >
              <Input.Password
                placeholder={t("admin_user.add_modal.input_confirm_password")}
                allowClear={true}
              />
            </Form.Item>
          </div>
          <div className="w-1/2 pl-[5px]">
            {/* ROLE */}
            <Form.Item
              name={"roleId"}
              label={t("admin_user.add_modal.role")}
              rules={[{ required: true, message: `${t("mess.required")}` }]}
              tooltip={{
                title: `${t("mess.required")}`,
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select onChange={onHandleChangeRole} options={roleOptions} />
            </Form.Item>
            {/* LOCATION */}
            {scopeType !== "GLOBAL" && (
            <Form.Item
              label={t("admin_user.add_modal.location")}
              required
              name={"location"}
              initialValue={location}
            >
              {" "}
              <>
                {multiSelectLocation ? (
                  <Select
                    mode="multiple"
                    onChange={(e, o) => onHandleChangeLocation(e, o)}
                    options={countryOptions}
                    value={showLocations}
                  />
                ) : (
                  <Select
                    onChange={(e, o) => onHandleChangeLocation(e, o)}
                    options={countryOptions}
                    value={showLocations}
                  />
                )}
              </>
            </Form.Item>
            )}
            <Form.Item
              label={t("admin_user.add_modal.admin_can_edit")}
              required
              className="[&_.ant-form-item-control-input-content]:text-left"
              name="adminCanEdit"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </Form.Item>
            <Form.Item
              label={t("admin_user.add_modal.upload_avatar")}
              className="[&_.ant-form-item-control-input-content]:text-left"
            >
              <div className="relative h-28 w-28">
                <Upload
                  accept="image/png, image/jpeg"
                  showUploadList={false}
                  maxCount={1}
                  beforeUpload={handleSelectAvatar}
                >
                  <div className="flex h-28 w-28 cursor-pointer items-center justify-center overflow-hidden rounded-md border border-dashed border-[var(--app-border)] hover:border-blue-500">
                    {avatarPreview || avatar ? (
                      <img
                        src={avatarPreview || avatar}
                        alt="avatar"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center text-[var(--app-text-muted)]">
                        <PlusOutlined />
                        <span className="mt-1 text-xs">{t("btn.upload")}</span>
                      </div>
                    )}
                  </div>
                </Upload>
                {(avatarPreview || avatar) && (
                  <CloseCircleFilled
                    className="absolute -right-2 -top-2 cursor-pointer text-lg text-red-500"
                    onClick={handleRemoveAvatar}
                  />
                )}
              </div>
            </Form.Item>
          </div>
        </div>
        {/* ASSIGN SALON (only for roles scoped to a salon group) */}
        {scopeType === "OUTLET" && (
        <Form.Item
          label={t("admin_user.add_modal.assign_salon")}
          className="w-full"
        >
          <Input
            allowClear
            placeholder={t("dashboard.filter.search")}
            value={salonSearch}
            onChange={(e) => setSalonSearch(e.target.value)}
            className="mb-2"
          />
          <div className="max-h-[240px] overflow-y-auto rounded border border-[var(--app-border)] p-2">
            {filteredSalons.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              filteredSalons.map((item) => (
                <div key={item.key}>
                  <Checkbox
                    checked={targetKeys.includes(item.key)}
                    onChange={(e) => onToggleSalon(item.key, e.target.checked)}
                  >
                    {item.title}
                  </Checkbox>
                </div>
              ))
            )}
          </div>
        </Form.Item>
        )}
      </Form>
      <Modal
        open={conflictPrompt !== null}
        title={t("admin_user.add_modal.country_conflict_title")}
        onCancel={() => setConflictPrompt(null)}
        footer={
          <Box display="flex" justifyContent="flex-end" gap={1}>
          <CancelButton onClick={() => setConflictPrompt(null)}>
            {t("btn.cancel")}
          </CancelButton>
          <ActionButton onClick={() => onChooseCountryMode("SHARE")}>
            {t("admin_user.add_modal.country_conflict_share")}
          </ActionButton>
          <ActionButton onClick={() => onChooseCountryMode("TRANSFER")}>
            {t("admin_user.add_modal.country_conflict_transfer")}
          </ActionButton>
          </Box>
        }
      >
        <ul className="mb-3 list-disc pl-5">
          {conflictPrompt?.conflicts.map((conflict) => (
            <li key={conflict.country_id}>
              <b>{countryName(conflict.country_id)}</b>:{" "}
              {conflict.holders.map((h) => h.full_name || h.email).join(", ")}
            </li>
          ))}
        </ul>
        <p>{t("admin_user.add_modal.country_conflict_share_hint")}</p>
        <p>{t("admin_user.add_modal.country_conflict_transfer_hint")}</p>
      </Modal>
    </Modal>
  );
};
export default AddAdmin;
